import { api } from "./client";

export interface NotificationChoices {
  email_enabled: boolean;
  telegram_enabled: boolean;
  daily_report: boolean;
  daily_plan: boolean;
  weekly_plan: boolean;
  delivery_hour: number;
}

export interface NotificationPreferencesResponse extends NotificationChoices {
  timezone: string;
  email: string;
  telegram_available: boolean;
  telegram_connected: boolean;
  /** True only after Telegram Start, before portal confirmation. */
  telegram_pending: boolean;
  telegram_display_name: string | null;
  telegram_confirmation_id: string | null;
  telegram_binding_revision: string | null;
  readonly_demo: boolean;
}

export interface NotificationPreferencesUpdate extends NotificationChoices {
  /** The exact connected account shown when Telegram delivery was selected. */
  telegram_binding_revision?: string;
}

export const NOTIFICATION_PREFERENCES_KEY = ["notification-preferences"];

export interface TelegramLink {
  url: string;
  expires_at: string;
}

export async function getNotificationPreferences(signal?: AbortSignal): Promise<NotificationPreferencesResponse> {
  const { data } = await api.get<NotificationPreferencesResponse>("/notifications/preferences", { signal });
  return data;
}

export async function saveNotificationPreferences(choices: NotificationPreferencesUpdate, signal?: AbortSignal): Promise<NotificationPreferencesResponse> {
  const { data } = await api.put<NotificationPreferencesResponse>("/notifications/preferences", choices, { signal });
  return data;
}

export async function createTelegramLink(signal?: AbortSignal): Promise<TelegramLink> {
  const { data } = await api.post<TelegramLink>("/notifications/telegram/link", {}, { signal });
  return data;
}

export async function confirmTelegramAccount(confirmationId: string, signal?: AbortSignal): Promise<NotificationPreferencesResponse> {
  const { data } = await api.post<NotificationPreferencesResponse>("/notifications/telegram/confirm", { confirmation_id: confirmationId }, { signal });
  return data;
}

export async function disconnectTelegram(signal?: AbortSignal): Promise<NotificationPreferencesResponse> {
  const { data } = await api.delete<NotificationPreferencesResponse>("/notifications/telegram", { signal });
  return data;
}

/** A link from an unexpected origin must never become a connection button. */
export function telegramLinkUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname === "t.me" && !url.port && !url.username && !url.password &&
      /^\/[A-Za-z0-9_]+$/.test(url.pathname) && url.searchParams.has("start") && !url.hash ? url.href : null;
  } catch { return null; }
}
